'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { dayjs } from '@src/corelib';
import { RouterLink } from '@src/components/core/link';
import paths from '@src/paths';

export default function NotificationsPopover({
  anchorEl,
  loading = false,
  notifications = [],
  onClose,
  onMarkAllAsRead,
  onNotificationClick,
  open = false,
}) {
  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      onClose={onClose}
      open={open}
      slotProps={{ paper: { sx: { width: '380px' } } }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
        <Typography variant="h6">Notifications</Typography>
        <Button disabled={!notifications.length || loading} onClick={onMarkAllAsRead} size="small" variant="text">
          Mark all as read
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary" variant="body2">
            Loading notifications...
          </Typography>
        </Box>
      ) : notifications.length === 0 ? (
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle2">There are no notifications</Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: '360px', overflowY: 'auto' }}>
          <List disablePadding>
            {notifications.map((notification, index) => (
              <NotificationItem
                divider={index < notifications.length - 1}
                key={notification.id}
                notification={notification}
                onClick={() => onNotificationClick?.(notification)}
              />
            ))}
          </List>
        </Box>
      )}
      <Box sx={{ borderTop: '1px solid var(--mui-palette-divider)', p: 2 }}>
        <Button
          component={RouterLink}
          fullWidth
          href={paths.notifications}
          onClick={onClose}
          variant="text"
        >
          View All Notifications
        </Button>
      </Box>
    </Popover>
  );
}

function NotificationItem({ divider, notification, onClick }) {
  const linkProps = notification.link
    ? { component: RouterLink, href: notification.link }
    : { component: 'button' };
  const content = (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: notification.is_read ? 500 : 700 }} variant="body2">
          {notification.title}
        </Typography>
        {!notification.is_read ? <Chip color="primary" label="New" size="small" variant="soft" /> : null}
      </Stack>
      {notification.body ? (
        <Typography color="text.secondary" variant="body2">
          {notification.body}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography color="text.secondary" variant="caption">
          {dayjs(notification.created_at).format('MMM D, hh:mm A')}
        </Typography>
        {notification.client?.name ? (
          <Typography color="text.secondary" variant="caption">
            {notification.client.name}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );

  return (
    <ListItem disablePadding divider={divider}>
      <ListItemButton
        {...linkProps}
        onClick={onClick}
        sx={{
          alignItems: 'flex-start',
          bgcolor: notification.is_read ? 'transparent' : 'var(--mui-palette-action-hover)',
          py: 1.5,
        }}
      >
        {content}
      </ListItemButton>
    </ListItem>
  );
}
